import {
  forceLoadMessagesAndLocale,
  loadMessagesAndLocale,
  loadMessagesAndLocaleCancellationPortal,
} from '@client/internationalization/load';
import type { Messages } from '@client/internationalization/types';
import type { Fetchable } from '@client/models/Fetchable';
import { fetchableHandle, FetchableNotStarted, FetchableSuccess, mapSuccess } from '@client/models/Fetchable';
import type { ActionsUnion } from '@client/utils/redux';
import { createAction, createPromiseAction } from '@client/utils/redux';
import type { Locale } from '../modules/app-context/constants';

export interface InternationalizationState {
  messages: Fetchable<Messages>;
  locale: Fetchable<Locale>;
}

const initialState: InternationalizationState = {
  messages: FetchableNotStarted,
  locale: FetchableNotStarted,
};

export enum ActionKeys {
  FETCH_MESSAGES_AND_LOCALE = 'INTERNATIONALIZATION/FETCH_MESSAGES_AND_LOCALE',
  FORCE_SET_MESSAGES_AND_LOCALE = 'INTERNATIONALIZATION/FORCE_SET_MESSAGES_AND_LOCALE',
}

export const InternationalizationActions = {
  forceFetchMessagesAndLocale: (locale: Locale) =>
    createPromiseAction(ActionKeys.FETCH_MESSAGES_AND_LOCALE, forceLoadMessagesAndLocale(locale)),
  fetchMessagesAndLocale: () => createPromiseAction(ActionKeys.FETCH_MESSAGES_AND_LOCALE, loadMessagesAndLocale()),
  fetchMessagesAndLocaleSupplierPortal: (locale: Locale) =>
    createPromiseAction(ActionKeys.FETCH_MESSAGES_AND_LOCALE, loadMessagesAndLocaleCancellationPortal(locale)),
  forceSetMessagesAndLocale: (messages: Messages, locale: Locale) =>
    createAction(ActionKeys.FORCE_SET_MESSAGES_AND_LOCALE, { messages, locale }),
};

type Actions = ActionsUnion<typeof InternationalizationActions>;

export function reducer(state: InternationalizationState = initialState, action: Actions): InternationalizationState {
  switch (action.type) {
    case ActionKeys.FETCH_MESSAGES_AND_LOCALE:
      return fetchableHandle(state, action, (fetchable) => ({
        ...state,
        messages: mapSuccess(fetchable, (d) => d.messages),
        locale: mapSuccess(fetchable, (d) => d.locale),
      }));

    case ActionKeys.FORCE_SET_MESSAGES_AND_LOCALE:
      return {
        messages: FetchableSuccess(action.payload.messages),
        locale: FetchableSuccess(action.payload.locale),
      };

    default:
      return state;
  }
}

interface State {
  internationalization: InternationalizationState;
}

export const selectorMessagesFetchable = (state: State) => state.internationalization.messages;
export const selectorLocaleFetchable = (state: State) => state.internationalization.locale;

export function selectorIsMessagesFetched(state: State): boolean {
  return Boolean(state.internationalization.messages);
}
